import { useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import "@/App.css";
import "@/index.css";
import { LanguageProvider } from "@/components/LanguageProvider";
import { AnalyticsProvider } from "@/components/AnalyticsProvider";
import SecurityOverlay from "@/components/SecurityOverlay";
import Navigation from "@/components/Navigation";
import HeroSection from "@/components/HeroSection";
import MuseumShowcase from "@/components/MuseumShowcase";
import GeologicalOrigins from "@/components/GeologicalOrigins";
import ElevatorPitch from "@/components/ElevatorPitch";
import SpecimenStats from "@/components/SpecimenStats";
import GallerySection from "@/components/GallerySection";
import StorySection from "@/components/StorySection";
import CertificationSection from "@/components/CertificationSection";
import DocumentViewer from "@/components/DocumentViewer";
import RaritySection from "@/components/RaritySection";
import AmbientVideo from "@/components/AmbientVideo";
import EmeraldEducation from "@/components/EmeraldEducation";
import PurchaseProcess from "@/components/PurchaseProcess";
import LearnMore from "@/components/LearnMore";
import Tagline from "@/components/Tagline";
import Footer from "@/components/Footer";
import AdminDashboard from "@/components/AdminDashboard";

/*
  NARRATIVE FLOW — Open Landing Page:
  PHASE 1 — VISUAL SHOWCASE:
  1. Hero → 2. Museum Showcase → 3. Specimen Stats → 4. Gallery → 5. CTA

  PHASE 2 — THE STORY:
  6. Geological Origins → 7. Elevator Pitch → 8. Story → 9. Certification
  → 10. Documents → 11. Rarity → 12. Ambient Video → 13. Education

  PHASE 3 — CLOSE:
  14. Purchase Process → 15. Learn More → 16. Enquiry
*/

function getVisitorId() {
  let id = localStorage.getItem("bridgette_vid");
  if (!id) {
    id = "anon-" + crypto.randomUUID();
    localStorage.setItem("bridgette_vid", id);
  }
  return id;
}

function ShowcasePage() {
  const visitorId = useRef(getVisitorId()).current;

  useEffect(() => {
    fetch("/api/track-visit", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ visitor_id: visitorId }) }).catch(() => {});
  }, [visitorId]);

  return (
    <AnalyticsProvider visitorId={visitorId}>
      <SecurityOverlay>
        <div className="min-h-screen bg-[#111110]">
        <div className="fixed top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-[#064E3B] to-transparent z-[60] opacity-60" />
        <Navigation />

        {/* PHASE 1 — VISUAL SHOWCASE */}
        <HeroSection />

        <MuseumShowcase />

        <SpecimenStats />

        <GallerySection />

        {/* PHASE 2 — THE STORY */}
        <GeologicalOrigins />

        <ElevatorPitch />

        <Tagline text="You are not looking at a gemstone. You are looking at something the Earth will never produce again." />

        <div className="section-divider my-4" />
        <StorySection />

        <CertificationSection />

        <DocumentViewer />

        <Tagline text="The last comparable specimen to change hands sold decades ago. The next may never." />

        <RaritySection />

        <AmbientVideo />

        <EmeraldEducation />

        {/* PHASE 3 — CLOSE */}
        <Tagline text="Objects of this significance do not have a market price. They have a moment — and it passes." />

        <PurchaseProcess />

        <LearnMore />

        <Footer />
      </div>
      </SecurityOverlay>
    </AnalyticsProvider>
  );
}

function App() {
  return (
    <LanguageProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<ShowcasePage />} />
          <Route path="/admin" element={<AdminDashboard />} />
        </Routes>
      </BrowserRouter>
    </LanguageProvider>
  );
}

export default App;
